/**
 * Geo policy for the Symphony package picker — a single config lever shared by the eligibility gate
 * and the price-geo selection, so the two never drift.
 *
 * `SYMPHONY_PICKER_ALLOWED_COUNTRIES` is a databag value (comma-separated ISO-3166-1 alpha-2 codes,
 * e.g. `"US,GB"`) surfaced to `process.env` by express-server.js. Opening a region is a deliberate
 * databag change, NOT a code change — so the code default stays the current prod state (US only).
 * Wix employees pass the same gate as everyone else (see resolvePickerEligibility).
 *
 * **The value is NOT this artifact's.** It is read cross-artifact from symphony-bff's
 * `agentPremiumSupportedCountries` — the ONE store the whole product now reads for "which markets can
 * buy" (the harness's plan-selector gate and the native apps' injected spec read the same key from the
 * same artifact). It used to live in orion-web's own Secrets databag, which made a market four manual
 * edits with nothing keeping them in step: the half-open states that produced were the agent
 * advertising a picker the web wall then 403s, or the wall opening onto a Free-only picker.
 *
 * Two consequences of it being someone else's key:
 *  - the erb `rescue`s it, because this artifact cannot create it (same reason the preview allowlist
 *    is rescued), and
 *  - a rescue would otherwise close every market SILENTLY, so an empty value is logged once per
 *    process rather than quietly resolving to the US default. Empty here means the databag read
 *    failed, not that someone chose US-only.
 *
 * Only list geos the catalog actually prices — but understand what an allowed-but-unpriced geo
 * actually gets, because it is NOT nothing: agent-payments substitutes the US ladder for a geo it
 * prices nowhere (`AgentPlanResolver.FALLBACK_GEO`), so the buyer sees the normal cards at USD
 * prices. See the note on `isMarketOpen` below, which states the same rule.
 *
 * **Israel is the one geo where that substitution does not happen** — it is in
 * `FALLBACK_EXCLUDED_GEOS` because IL is charged through a manual shekel VAT rate keyed on the
 * buyer's geo, which would fire on top of a USD price. So IL is the only market where losing its
 * own prices leaves the catalog empty, and — now that the picker offers paid cards only — renders
 * "No plans are available right now" where the Free card used to stand.
 *
 * An employee in a geo sees exactly what a real buyer there sees; previewing another market is what
 * the allowlist-gated `?country=` param is for (see lib/preview-allowlist).
 */

const DEFAULT_ALLOWED_COUNTRIES = ['US'];

/** The geo whose price we fall back to for any country we don't (or can't) price per-geo. */
export const DEFAULT_PRICING_GEO = 'US';

/**
 * Countries no market may ever be opened to. IN CODE on purpose, and duplicated on purpose: this is
 * the third copy of the same list, beside `SiteMarket.DENIED_COUNTRIES` (symphony-bff) and
 * `AgentPaymentsServiceImpl.DENIED_GEOS` (agent-payments). All three are code rather than config
 * precisely so that no databag edit can open a sanctioned market — which is the one thing the held
 * list below is allowed to do.
 *
 * That means they are kept in step by hand, and the failure mode is real: Syria was missing from the
 * server pair for a day. Change one, change all three.
 */
const DENIED_COUNTRIES = new Set(['BY', 'CU', 'IR', 'KP', 'RU', 'SY']);

/** The deny list, for the staff debug panel — so a shut market can say WHY it is shut. Read-only copy. */
export function deniedPickerCountries(): Set<string> {
  return new Set(DENIED_COUNTRIES);
}

/**
 * Countries WITHHELD from sale: sellable in principle, not cleared yet — an e-invoicing regime we
 * cannot satisfy, a tax-ID format Stripe will not take, a market with no local price where policy
 * requires one.
 *
 * Read from the SAME databag key the servers read (`heldGeos` on agent-payments), so the picker and
 * the charge can never disagree about what is held. Blank/unset → nothing held, and that is correct
 * rather than fail-closed: what may never be opened by accident is the deny list above, which config
 * cannot reach.
 */
export function heldPickerCountries(): Set<string> {
  return new Set(
    (process.env.SYMPHONY_PICKER_HELD_COUNTRIES ?? '')
      .split(',')
      .map((c: string) => c.trim().toUpperCase())
      .filter(Boolean),
  );
}

/**
 * **The gate.** Open to every country except the sanctioned ones and the withheld ones — the deny-list
 * rule the servers adopted, now on the surface that actually enforces it. This matters more than it
 * looks: the native apps' own gate only chooses which URL to open, and the one they open is this app's
 * `/packages`, so every client purchase is allowed or refused right here.
 *
 * **A missing country is CLOSED**, unchanged from the allowlist this replaces and still right for the
 * same reason: we cannot conclude a buyer is outside a sanctioned market if we cannot tell where they
 * are. Fail-closed on the deny list, never fail-open.
 *
 * An open country is not necessarily a PRICED one. That is deliberate — an unpriced open market
 * resolves the US ladder server-side (`AgentPlanResolver.FALLBACK_GEO`) rather than showing nothing.
 * Before opening a market, check it is not REGISTERED-but-UNPRICED in Stripe Tax: those get tax added
 * on top of a USD price this file reports as tax-inclusive. See agent-premiums `operations.md`.
 */
export function isMarketOpen(countryCode: string): boolean {
  const code = (countryCode ?? '').trim().toUpperCase();
  if (!code) return false;
  return !DENIED_COUNTRIES.has(code) && !heldPickerCountries().has(code);
}

/**
 * Warn once per process that the shared countries key resolved empty. Not per call: this runs on the
 * debug panel's path, and the condition is a process-lifetime property of the config.
 */
let warnedMissingCountries = false;

function warnMissingCountriesOnce(): void {
  if (warnedMissingCountries) return;
  warnedMissingCountries = true;
  console.warn(
    '[picker-geo] SYMPHONY_PICKER_ALLOWED_COUNTRIES is empty. This is no longer the region gate — see ' +
      '`isMarketOpen` — so nothing is closed by it resolving empty. It is the list of markets the ' +
      "catalog prices, read cross-artifact from symphony-bff's agentPremiumSupportedCountries, and it " +
      'only feeds the staff debug panel now. Empty means that read failed, not that US-only was chosen.',
  );
}

/**
 * The markets the catalog prices, from the databag. **This is NOT the gate any more** — it was, and
 * `isMarketOpen` replaced it when the deny list shipped. It survives because the staff debug panel
 * offers these countries for preview and labels the rest, which is a genuinely different question from
 * "may this buyer purchase".
 *
 * Falls back to US on an empty read, which now means "the panel offers one country" rather than "every
 * market is shut".
 */
export function allowedPickerCountries(): Set<string> {
  const raw = process.env.SYMPHONY_PICKER_ALLOWED_COUNTRIES;
  if (!raw || !raw.trim()) warnMissingCountriesOnce();
  const codes = (raw ? raw.split(',') : DEFAULT_ALLOWED_COUNTRIES)
    .map((c: string) => c.trim().toUpperCase())
    .filter(Boolean);
  return new Set(codes.length ? codes : DEFAULT_ALLOWED_COUNTRIES);
}

/**
 * The visitor's country → a geo we price for: the country itself when it's an allowed geo, else the
 * default (US). Keeps the priced geo in lock-step with the gate off one config (a US visitor and a
 * Wix employee in an unpriced country both get US prices).
 */
export function pricingGeoForCountry(countryCode: string): string {
  return isMarketOpen(countryCode) ? countryCode : DEFAULT_PRICING_GEO;
}

/**
 * The EU member states priced in euros: the 21 of the euro area, plus Denmark. Bulgaria belongs to the
 * euro area proper — it adopted the euro on 2026-01-01 and the lev stopped being legal tender on
 * 2026-02-01.
 *
 * **Denmark is the deliberate exception**, and the reason is the peg, not convenience. The krone has
 * tracked the euro since 1982 — ERM II central rate 7.46038, a formal ±2.25% band that Nationalbanken
 * holds nearer ±0.5% as standing policy — so the FX drift that local-currency pricing exists to absorb
 * is near nil for DKK, unlike the genuinely floating CZK/HUF/PLN/RON. Pricing DK in euros costs a
 * Danish buyer almost nothing real and buys back nine Stripe prices, a row in every future re-pricing,
 * and the `kr` collision with SEK (see `money.ts`) that existed only because both were priced.
 *
 * The five members that DO keep their own currency are listed individually in `LOCAL_CURRENCY_GEOS`;
 * putting them here would quote euros to a market charged in korunas.
 */
const EUR_GEOS = new Set([
  'AT', 'BE', 'BG', 'CY', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR',
  'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PT', 'SI', 'SK',
]);

/**
 * ISO currency for a pricing geo — a display-only fallback for the callers that have no resolved
 * Stripe price to read the currency off: the `currency` on a `/api/plans` response that priced
 * nothing, a coupon validated before a price resolved, and the staff picker-debug panel. The
 * authoritative currency is always the one Stripe returns on the resolved price; this only fills
 * the gap.
 *
 * A geo absent from here falls back to USD, which is also what `pricingGeoForCountry` resolves an
 * unlisted country to — so the two stay consistent. Opening a market therefore means adding it to
 * the databag AND to the currency it is priced in, or the market quotes dollars at a buyer who is
 * charged in euros.
 *
 * **BRAZIL IS THE ONE EXCEPTION, AND IT IS DELIBERATE: `usd` is the right answer for BR, not a
 * missing mapping.** Every other market is billed in its own currency, so for years "a geo resolves
 * usd" and "somebody forgot this geo" were the same statement, and the guards in `picker-geo.test.ts`
 * are built on that. Brazil is billed in **US dollars** at the US ladder, so it must be ABSENT here
 * (adding `BR: 'brl'` back quotes reais at a buyer Stripe charges in dollars — the same class of bug
 * in reverse), because Stripe Tax has no Brazil at all and Stripe flags BRL under FX control.
 *
 * MY/KE/IS/VN briefly shared that USD set and have been moved to their own currencies: USD costs a
 * local cardholder the issuer's FX spread and authorizes worse, and only Brazil actually has to pay
 * that. Brazil is alone on the USD set again, which is what it was for.
 *
 * Two consequences worth knowing:
 *  - BR is absent from `PRICED_GEOS` too, which is correct: that list exists so the debug preview can
 *    offer the geos this file knows a currency for, and a USD-priced geo is by construction
 *    indistinguishable from the fallback. Previewing `?country=BR` still works and still prices BR.
 *  - The allowed-market guard in the tests names BR explicitly rather than assuming the fallback means
 *    a bug. Add the next USD-billed market to that set; do NOT relax the assertion.
 *
 * The five floating-currency EU members (CZ, HU, PL, RO, SE) are billed in their own currency and
 * listed individually below — pegged Denmark is priced in euros instead, see `EUR_GEOS`. Their symbols
 * trail the amount; `formatMoney` handles that (see `money.ts`).
 */
const LOCAL_CURRENCY_GEOS: Record<string, string> = {
  GB: 'gbp',
  CA: 'cad',
  CZ: 'czk',
  HU: 'huf',
  PL: 'pln',
  RO: 'ron',
  SE: 'sek',
  AU: 'aud',
  NZ: 'nzd',
  IL: 'ils',
  IN: 'inr',
  UA: 'uah',
  // Kazakhstan. Priced 2026-08-20 at 10,710 ₸ Basic, which nets $20.01 after the 16% VAT — Stripe's
  // own rate via a tax calculation, not the 12% headline figure a search returns.
  //
  // Pricing it was urgent rather than routine: it already had an ACTIVE Stripe Tax registration while
  // carrying no price, and that pair is the one combination that silently over-charges. The USD
  // fallback would have served the US ladder (tax-EXCLUSIVE by construction) while this file reports
  // every non-US/CA geo as tax-inclusive, so a buyer would have seen $20 and been charged $20 + 16%.
  //
  // No seller tax id is recorded for KZ, and that is fine here: Yanit Vaknin (Wix tax director) ruled
  // Kazakhstan does not require it on invoices. Written down because the absence looks like the
  // half-finished state every other market in the 2026-08-19 batch avoided — each of those got a
  // registration AND an id together — and the next person to notice will otherwise re-litigate it.
  KZ: 'kzt',
  // One of the two currencies whose Stripe amount needs NO division (see KR below): a Stripe JPY
  // amount is already whole yen. Every minor-unit conversion goes through common-lib
  // `minorToMajorUnits` for that reason — a hardcoded /100 renders a ¥3,000 plan as "¥30" while
  // checkout charges the full amount.
  JP: 'jpy',
  // The five markets mapped alongside the EU/CA wave, all rated `All PTCs` by Stripe Tax — the same
  // tier as US/GB/EU/CA — so they need no special taxability handling.
  //
  // Switzerland lagged the other four: the number first supplied (`052.0147.3940`) is an ESTV dossier
  // reference, not the `CHE-XXX.XXX.XXX MWST` UID Stripe accepts, so CH sat mapped-but-priceless for
  // two days. **Resolved 2026-08-11** — the real UID (`ch_vat`) is on the account and the CH tax
  // registration is active, so CH now collects its 8.1% like everywhere else. The lesson worth keeping:
  // with `automatic_tax` on unconditionally, a market whose registration is missing collects **zero**
  // tax we still owe while the invoice reports `complete`. That is invisible at runtime, so the
  // registration — not the tax ID, they are separate objects — is what gates opening a market.
  ZA: 'zar',
  CH: 'chf',
  AE: 'aed',
  SG: 'sgd',
  NO: 'nok',
  // Korea and Thailand, opened 2026-08-11 alongside Switzerland: `kr_brn` / `th_vat` on the account,
  // active Stripe Tax registrations, and 9 prices each.
  //
  // **KRW is the second no-division currency**, joining JPY above — see the note in `money.ts`.
  //
  // **Taiwan is mapped and deliberately CLOSED.** Not a Stripe limitation — Stripe Tax takes a TW
  // registration and `tw_vat` is on the account — but Taiwan additionally requires a Government
  // Uniform Invoice (eGUI, 電子發票) per sale, which a Stripe invoice is not. Held pending that answer;
  // the entry is inert meanwhile, exactly as CH's was.
  //
  // ⚠️ **Every price for these geos is `tax_behavior: inclusive` with a round gross amount, and must
  // stay that way.** This is the third decision named below (see TAX_EXCLUSIVE_GEOS), and adding a geo
  // here silently answers it: absent from that set means the picker tells the buyer the price includes
  // tax. Price one `unspecified` — which inherits the account default, `exclusive` — and a Korean buyer
  // is shown ₩33,000 "including tax" and then debited ₩36,300. Nothing in the code can detect the
  // mismatch, because `tax_behavior` is not carried on `/api/plans`; it has to be right in Stripe.
  KR: 'krw',
  TW: 'twd',
  TH: 'thb',
  // Malaysia, Kenya, Iceland and Vietnam. They spent a few hours on Brazil's USD price set, which was
  // the wrong call: charging a local cardholder in dollars adds the issuer's FX spread and authorizes
  // materially worse, and unlike Brazil — where Stripe Tax has no jurisdiction and BRL is flagged
  // under FX control — all four can be billed locally, like every other market we collect tax in.
  //
  // ⚠️ **ISK and VND are opposite minor-unit cases and sit here together on purpose.** A Stripe VND
  // amount is already whole dong (`vnd` IS in common-lib STRIPE_NO_DIVISION_CURRENCIES); ISK is a
  // zero-decimal CURRENCY whose Stripe amounts are still x100, so it is deliberately NOT in that set.
  // Swap either and one market renders 100x off, silently. See `money.ts`.
  MY: 'myr',
  KE: 'kes',
  IS: 'isk',
  VN: 'vnd',
  // Six markets whose Stripe Tax registrations went active 2026-08-19 (Georgia 2026-08-15) and were
  // verified calculating live: GE 18%, CL 19%, TR 20%, NG 7.5%, PE 18%, TZ 18%. Registration is what
  // gates opening a market — see the Switzerland note above — so the tax half is done for all six.
  //
  // **Only Georgia and Chile are cleared to open.** The other four are mapped and deliberately CLOSED,
  // each on something a tax registration does not answer:
  //   TR  Turkey's e-Arsiv: whether a non-resident e-service filer must issue Turkish e-invoices is
  //       unresolved, and Turkey shipped new e-Fatura/e-Arsiv standards on 2026-02-02. Taiwan's shape.
  //   NG  Nigeria's FIRS e-invoicing is expected to pull in non-residents during 2026, no date.
  //   PE  Peru registers non-residents as a WITHHOLDING AGENT, not a simplified VAT payer, and the
  //       return is due 10 days after month end. Also a 2026 split: IGV 16->14, IPM 2->4, total 18.
  //   TZ  Tanzania charges 18% VAT *and* a 3% Digital Service Tax on gross that Stripe Tax has no
  //       concept of. Registering made this worse, not better: VAT now flows through Stripe while the
  //       DST accrues with nothing surfacing it. Do not open TZ until finance owns that monthly return.
  //
  // ⚠️ **CLP is the third no-division currency** (with JPY and KRW above, plus VND): `clp` IS in
  // common-lib STRIPE_NO_DIVISION_CURRENCIES, so a Stripe CLP amount is already whole pesos. It is also
  // a DOLLAR-SIGN currency, which is the sharper trap — see the `clp` entry in `money.ts`.
  //
  // ⚠️ **TRY needs re-pricing, unlike every other entry here.** Turkish inflation moves the lira fast
  // enough that a fixed ladder drifts off its USD target within months. Local currency is still right
  // (dollars cost a local cardholder the issuer's FX spread, the Brazil argument), but re-check the TRY
  // ladder against ~$20 net-of-VAT for Basic whenever Turkey is reviewed, and before it opens.
  GE: 'gel',
  CL: 'clp',
  TR: 'try',
  NG: 'ngn',
  PE: 'pen',
  TZ: 'tzs',
  // Mexico. Priced 2026-08-21 at MX$340 Basic / MX$3,240 yearly, which nets $20.06 at 16.95 MXN/USD.
  //
  // It is priced rather than left on the USD fallback because Mexico is on the list of markets where
  // policy requires the price be SHOWN AND CHARGED in local currency — the same reason Brazil is the
  // outstanding exception, being charged in dollars while sitting on that list.
  //
  // ⚠️ **The amount carries NO tax gross-up, and that is a live assumption, not a constant.** Yanit
  // Vaknin (Wix tax director) ruled Mexico out of scope — it is absent from the VAT mapping sheet — and
  // Stripe agrees today: an MX tax calculation returns `not_collecting`, meaning Stripe knows the
  // jurisdiction and collects nothing because we hold no registration. If a Mexican registration is
  // ever activated, `tax_behavior=inclusive` makes Stripe carve 16% out of these amounts and the net
  // drops to $17.29. The ladder must be re-struck to 390/3,744 the same day that happens.
  //
  // The yearly is the one entry NOT on the house `monthly * 96 // 10` formula: 3,240 instead of 3,264,
  // asked for as a rounder number, which deepens the discount to 20.6% and takes the whole yearly
  // ladder to 8,100/16,200/32,400.
  MX: 'mxn',
};

export function currencyForGeo(geo: string): string {
  return LOCAL_CURRENCY_GEOS[geo] ?? (EUR_GEOS.has(geo) ? 'eur' : 'usd');
}

/**
 * The two markets whose picker prices EXCLUDE tax, so tax is added at checkout.
 *
 * Everywhere else the number shown is the number charged. North America is the deliberate exception,
 * on convention rather than oversight: both the US and Canada quote pre-tax and add tax at the till,
 * and neither requires an inclusive consumer price — where the EU, UK, AU, NZ, SG, JP, IN, ZA and the
 * rest either require it or expect it.
 *
 * Both are also the two markets where ONE inclusive price could not be right anyway: US sales tax
 * varies by state, and Canada runs from 5% GST-only to 15% HST by province, so a single gross amount
 * would swing net revenue by about ten points depending where the buyer lives.
 *
 * **Inclusive-ness is reached two different ways, and only one of them is the price.** Audited against
 * the live catalog:
 *
 *  - **17 geos store it on the price** — `tax_behavior: inclusive` with a round gross amount, so
 *    displayed = stored = debited (EU, GB, SE, CZ, PL, RO, HU, NO, ZA, SG, AE, NZ, UA, AU, JP, IN).
 *  - **Israel does NOT.** Its prices are `tax_behavior: unspecified`, which inherits the account
 *    default — and that default is `exclusive`. What makes ₪70 the total is a **manual inclusive
 *    TaxRate** (18%, `inclusive: true`), used because Stripe Tax does not support Israel at all. So IL
 *    belongs on the inclusive side for a reason that lives in a TaxRate rather than in the price, and
 *    a **dormant exclusive twin of that rate exists** in the same account: activating it instead makes
 *    the Israeli disclaimer false with no code change and nothing here to catch it.
 *  - **US and CA prices are also mostly `unspecified`** rather than literally `exclusive` — same
 *    inheritance, opposite conclusion. "Unspecified" reads like "unknown"; it means "the account
 *    default", so it must be resolved against `/v1/tax/settings` and never taken at face value.
 *  - **Brazil is inclusive because NO tax is ever calculated there** — a third mechanism again. Brazil
 *    is absent from Stripe Tax entirely (not a missing registration; Stripe cannot calculate BR tax for
 *    any account), so `automatic_tax` reports `complete` and adds nothing. The shown price is therefore
 *    the charged price, and the tax-silent disclaimer is true — but it is true by vacuum, not because
 *    the amount contains tax.
 *
 *  - **Malaysia, Kenya, Iceland and Vietnam DO collect**, unlike Brazil, and are billed in their own
 *    currencies with round gross inclusive amounts like every other collecting market. Stripe Tax
 *    supports all four (probed: `not_collecting`, i.e. supported-but-unregistered, where Brazil returns
 *    `not_supported`), so once each registration exists the tax comes out of the shown price. Nothing
 *    in this file can see whether a registration exists, which is why the databag must not open one of
 *    the four before its registration lands — that state is Israel's old bug exactly.
 *
 *    **Vietnam grants no B2B exemption at all** — the only such row in the Wix VAT mapping ("filed for
 *    invoicing purposes only") — and **Stripe already honours that; we do not have to enforce it.**
 *    Stripe's customer-tax-ID table carries a per-type "impact in tax calculation" flag ("Stripe Tax
 *    won't apply tax if this tax ID is provided"), and `vn_tin` is flagged NO: a Vietnamese buyer who
 *    supplies a tax ID is still charged the 10%, and the id lands on the invoice, which is exactly what
 *    the mapping asks for. Malaysia is the same (`my_frp`/`my_itn`/`my_sst` all NO, matching its blank
 *    exemption field); Iceland's `is_vat` is YES, matching its.
 *
 *    Verified live rather than taken from the doc, on Japan — registered, and carrying two id types
 *    with opposite flags: no id -> 10% `standard_rated`, `jp_trn` (YES) -> 0% `not_subject_to_tax`,
 *    `jp_cn` (NO) -> 10% `standard_rated`. So the flag is observable, and Vietnam's is the safe one.
 *
 *    ⚠️ **This is Stripe's behaviour, not ours, and no test here can assert it** — `tax_id_collection`
 *    is enabled account-wide with no per-country switch, so if Stripe ever re-flags `vn_tin` to YES we
 *    silently stop collecting Vietnamese VAT we still owe. Re-check that flag when Vietnam's
 *    registration goes in, and on any Stripe Tax change touching APAC.
 *
 *    **Kenya disagrees with the mapping in the other direction:** `ke_pin` is flagged NO while the
 *    sheet lists PIN as Kenya's exemption field, so a Kenyan business supplying its PIN is charged the
 *    16% anyway. That over-collects rather than under-collects — a buyer complaint, not a liability —
 *    so it does not gate opening Kenya, but the sheet and Stripe cannot both be right.
 *
 *    **Collecting nothing in Brazil is APPROVED, not an oversight** — signed off by Wix's tax director
 *    (2026-08-11), consistent with how Base44 already sells there. That matters to write down because
 *    the shape is indistinguishable from the Israeli bug that preceded it: an `automatic_tax=complete`
 *    invoice collecting zero on a real sale. There it was a hole; here it is the decision. Nothing in
 *    Stripe or in this code can tell those two apart, so if you find yourself about to raise a
 *    compliance alarm on BR, this is the answer. Israel, Switzerland and every other market still
 *    collect, and a NEW market collecting nothing is still a bug until finance says otherwise.
 *
 * **Opening a market therefore needs a THIRD decision beside the databag and the currency:** does its
 * catalog charge the shown price, or add tax to it? Nothing in the code can answer that — `tax_behavior`
 * is not carried on the `/api/plans` payload (or anywhere in the TS clients), so a market opened
 * exclusive and forgotten here silently claims to be tax-inclusive.
 *
 * **This is display policy, not tax calculation.** Stripe decides and charges the tax either way; this
 * only decides which disclaimer the picker shows, because a paragraph promising "prices do not include
 * applicable taxes" is false in the markets where they now do.
 */
const TAX_EXCLUSIVE_GEOS = new Set(['US', 'CA']);

/**
 * Whether the price the picker shows for this geo already includes tax.
 *
 * Fails toward INCLUSIVE for an unknown geo, which is the safer direction for the claim being made:
 * the inclusive disclaimer simply says nothing about tax, whereas the exclusive one asserts tax is
 * extra. Silence about tax is never false; asserting the wrong thing is.
 */
export function showsTaxInclusivePrices(geo: string | undefined): boolean {
  return !TAX_EXCLUSIVE_GEOS.has((geo ?? '').trim().toUpperCase());
}

/**
 * Every geo this file knows a currency for — i.e. every market the catalog is expected to price.
 * It is NOT the allowed set (that is the databag) and carries no permission: it exists so the debug
 * panel's country preview can offer the markets we price without hardcoding a second list that
 * drifts from `currencyForGeo`. A country outside it previews at its own geo anyway and will simply
 * resolve no paid price — which is precisely the "allowed but unpriced" state worth seeing.
 */
export const PRICED_GEOS: readonly string[] = [
  DEFAULT_PRICING_GEO,
  ...Object.keys(LOCAL_CURRENCY_GEOS),
  ...EUR_GEOS,
]
  .filter((geo, i, all) => all.indexOf(geo) === i)
  .sort();
